import useSWRMutation from "swr/mutation";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Chip } from "@/components/badge/chip";
import { RowCard } from "@/components/list/row-card";
import { RowIndex } from "@/components/list/row-index";
import { useToast } from "@/hooks/use-toast";
import { toastError } from "@/lib/toast";
import { postFetcher, putFetcherKeyed } from "@/lib/swr";
import type { Subscription } from "./webhook-events";
import { WebhookDialog } from "./webhook-dialog";

/** POST /…/webhooks/:id/test 的同步投递结果(见 server webhook.service) */
interface DeliveryOutcome {
  ok: boolean;
  deliveryId: string;
  status?: number;
  error?: string;
}

// One subscription row: url + event chips + enable toggle + edit dialog.
// showPid: admin list mixes global/app rows; App Settings pins one app.
export const SubscriptionRow = ({
  sub,
  index,
  onChanged,
  apiBase,
  fixedPid,
}: {
  sub: Subscription;
  index: number;
  onChanged: () => void;
  apiBase: string;
  fixedPid?: string;
}) => {
  const { toast } = useToast();
  const { trigger: toggle, isMutating: toggling } = useSWRMutation(
    `${apiBase}/${sub.id}#toggle`,
    putFetcherKeyed,
    { onSuccess: () => onChanged() }
  );
  // Test 投递:发一条 webhook.test 到回调地址,同步拿投递结果
  const { trigger: sendTest, isMutating: testing } = useSWRMutation(
    `${apiBase}/${sub.id}/test`,
    postFetcher<DeliveryOutcome>,
    {
      onSuccess: (out) =>
        out.ok
          ? toast({
              title: `Test delivered (HTTP ${out.status})`,
              description: `deliveryId: ${out.deliveryId}`,
            })
          : toast({
              variant: "destructive",
              title: out.status
                ? `Test failed: HTTP ${out.status}`
                : "Test failed",
              description: out.error ?? "回调地址返回非 2xx。",
            }),
      onError: (e) => toastError(toast, "Test failed", e),
    }
  );

  return (
    <RowCard>
      <RowIndex n={index + 1} />
      <div className="flex min-w-0 flex-1 flex-col gap-0.5">
        <p className="truncate text-sm font-medium leading-none">{sub.url}</p>
        <div className="flex flex-wrap items-center gap-1">
          {sub.events.map((e) => (
            <Chip key={e} tone="blue">
              {e}
            </Chip>
          ))}
          {!fixedPid && sub.pid && <Chip tone="neutral">app: {sub.pid}</Chip>}
          {!sub.enabled && <Chip tone="orange">disabled</Chip>}
        </div>
      </div>
      {sub.creator?.name && (
        <span className="shrink-0 text-xs text-muted-foreground">
          by {sub.creator.name}
        </span>
      )}
      <div className="flex shrink-0 items-center gap-2">
        <Button
          variant="outline"
          size="sm"
          disabled={testing}
          onClick={() => sendTest()}
        >
          {testing ? "Testing…" : "Test"}
        </Button>
        <Switch
          checked={sub.enabled}
          disabled={toggling}
          onCheckedChange={(v) => toggle({ enabled: v })}
        />
        <WebhookDialog
          sub={sub}
          onSaved={onChanged}
          apiBase={apiBase}
          fixedPid={fixedPid}
        />
      </div>
    </RowCard>
  );
};
